import axios from "axios";

/**
 * BFF(토큰 핸들러) 전환 스위치.
 *
 * ── 무엇이 달라지는가
 *
 * 지금은 이 화면이 토큰을 직접 들고 있다(oidc-client-ts, sessionStorage). BFF 를 켜면
 * 게이트웨이가 토큰을 서버 세션에 보관하고 브라우저에는 세션 쿠키만 온다. 화면은
 * 토큰을 다루지 않는다.
 *
 * ── 왜 스위치인가
 *
 * 한 번에 바꾸면 되돌릴 방법이 없다. 게이트웨이가 두 방식을 함께 받도록 되어 있으므로
 * (Authorization 헤더가 있으면 그것을, 없으면 세션을), 화면도 배포 단위로 골라 쓸 수
 * 있어야 한다.
 *
 * 끄면 지금까지와 완전히 같다.
 *
 * ── 켤 때 함께 확인할 것
 *
 * 게이트웨이에서 atom.gateway.bff.enabled=true 여야 한다. 화면만 켜면 인증 헤더도
 * 세션도 없는 요청이 되어 전부 401 이 된다.
 */
export const BFF_ENABLED = process.env.REACT_APP_BFF_ENABLED === "true";

/** 게이트웨이 주소의 앞부분. 화면과 같은 오리진이라 경로만 있으면 된다. */
const API_BASE = "/api";

/**
 * 로그인이 방금 실패했음을 게이트웨이가 알리는 표시.
 *
 * 실패 시 게이트웨이가 이 표시를 달아 화면으로 돌려보낸다.
 */
const LOGIN_FAILED_MARK = "login=failed";

/** 방금 로그인에 실패해서 돌아온 것인가. */
export function isLoginFailure(): boolean {
  return window.location.search.includes(LOGIN_FAILED_MARK);
}

/** 로그인 없이 보는 화면인가(지금은 가입 화면뿐). 주소가 해시 방식이라 hash 로 본다 */
export function isPublicScreen(): boolean {
  return (window.location.hash ?? '').startsWith('#/sign-up');
}

/**
 * 로그인을 시작한다.
 *
 * <b>브라우저를 이동시켜야 한다.</b> XHR 로 부르면 인증 서버의 로그인 화면 HTML 이
 * 응답으로 올 뿐이고, 사용자에게는 아무 일도 일어나지 않는다.
 *
 * ★ 방금 실패해서 돌아온 경우에는 다시 보내지 않는다. 실패가 계속되는 상황에서
 *   무한히 로그인으로 튕기게 되고, 사용자는 화면이 깜빡이는 것만 보게 된다.
 *   한 번 멈추면 사용자가 새로고침으로 다시 시도할 수 있다.
 *
 * @return 실제로 이동시켰으면 true
 */
export function bffLogin(): boolean {
  if (isPublicScreen()) {
    // 가입 화면은 로그인 없이 쓴다. 여기서 부른 API 가 401 을 받아도 로그인으로 보내지 않는다.
    return false;
  }
  if (loggingOut) {
    // 로그아웃하는 중이다. 여기서 로그인으로 보내면 로그아웃 이동을 덮어쓴다
    // ({@link bffLogout} 참조). 화면은 그대로 멈춰 있어야 하므로 "이동한다"고 답한다.
    return true;
  }
  if (isLoginFailure()) {
    return false;
  }
  if (navigatingToLogin) {
    // 이미 보내는 중이다. 호출자에게는 "이동한다"가 맞다 — 화면은 그대로 멈춰 있어야
    // 하고, 오류를 그리면 안 된다.
    return true;
  }
  navigatingToLogin = true;
  window.location.assign(`${API_BASE}/bff/login`);
  return true;
}

/**
 * 이미 로그인으로 보내는 중인가.
 *
 * ── 왜 필요한가 (실측)
 *
 * 화면이 뜰 때 로그인을 시작하는 자리가 둘이다 — 가드({@link isLoginFailure} 참조)와
 * 401 인터셉터다. 로그인 안 된 상태로 들어오면 둘이 <b>거의 동시에</b> 이 함수를
 * 부른다. 게이트웨이 로그에서 한 번 로드에 /bff/login 이 2회 찍히는 것을 확인했다.
 *
 * 두 번 보내면 인가 요청도 두 번 만들어진다. 브라우저는 그중 하나만 따라가므로,
 * 돌아온 state 가 마지막에 저장된 것과 어긋나면 인증 서버까지 다녀와 놓고
 * <b>authorization_request_not_found</b> 로 실패한다. 게이트웨이를 재시작한 직후
 * 두 번 재현했고, 사용자에게는 "로그인이 완료되지 않았습니다"만 보인다.
 *
 * 경합이라 늘 실패하지는 않는다 — 그래서 더 나쁘다. 간헐적으로만 터진다.
 */
let navigatingToLogin = false;

/**
 * 로그아웃을 시작했는가.
 *
 * ── 왜 필요한가 (운영에서 확인, 2026-10-06: 로그아웃 버튼을 두 번 눌러야 했다)
 *
 * 로그아웃 버튼은 화면의 사용자 정보를 먼저 지운다. 그러면 가드가 사용자 정보를 다시
 * 조회하는데, 이 조회가 세션이 끊긴 직후에 도착하면 401 을 받고 인터셉터가 로그인으로
 * 보낸다. 그 이동이 <인증 서버 로그아웃 주소로 가려던 이동을 덮어쓴다>.
 *
 * 결과: 게이트웨이 세션만 끊기고 인증 서버 세션은 남는다. 브라우저는 로그인 경로로
 * 가서 비밀번호 없이 곧바로 다시 로그인되어 돌아온다 — 사용자에게는 로그아웃 버튼이
 * 아무 일도 하지 않은 것으로 보인다.
 */
let loggingOut = false;

/** 게이트웨이가 내려주는 CSRF 쿠키 이름. 스프링 기본값이다. */
const CSRF_COOKIE = "XSRF-TOKEN";

/** 게이트웨이가 기대하는 CSRF 헤더 이름. 스프링 기본값이다. */
export const CSRF_HEADER = "X-XSRF-TOKEN";

/**
 * 쓰기 요청에 붙일 CSRF 헤더.
 *
 * ── 왜 따로 필요한가
 *
 * axios 는 이 일을 스스로 한다(xsrfCookieName/xsrfHeaderName). 하지만 axios 를 거치지
 * 않는 곳이 있다 — 대표적으로 그리드다. 그리드는 자체 HTTP 계층으로 저장·삭제를
 * 보내므로, 여기서 만든 헤더를 직접 넘겨줘야 한다.
 *
 * 붙이지 않으면 조회는 되는데 저장만 403 이 된다. 화면 쪽을 아무리 봐도 원인이 보이지
 * 않는 종류의 증상이다.
 *
 * BFF 를 쓰지 않으면 빈 객체다 — 그때는 Bearer 로 인증하므로 CSRF 가 필요 없다.
 */
export function csrfHeader(): Record<string, string> {
  if (!BFF_ENABLED) {
    return {};
  }
  const token = readCookie(CSRF_COOKIE);
  return token ? { [CSRF_HEADER]: token } : {};
}

function readCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : undefined;
}

/** 지금 로그인한 사람. 로그인하지 않았으면 401 이라 undefined 를 돌려준다. */
export async function fetchBffSession(): Promise<BffSession | undefined> {
  try {
    const response = await axios.get<BffSession>(`${API_BASE}/bff/me`);
    return response.data;
  } catch {
    // 401 이거나 게이트웨이에 닿지 못했다. 둘 다 '로그인하지 않음'으로 다룬다 —
    // 화면이 할 수 있는 일이 로그인으로 보내는 것 하나로 같다.
    return undefined;
  }
}

/**
 * 세션을 끊는다.
 *
 * 게이트웨이 세션을 지우면 그 안의 토큰도 함께 사라진다. 브라우저에는 애초에 토큰이
 * 없으므로 지울 것이 없다 — 기존 방식에서 sessionStorage 를 비우는 것을 잊어 토큰이
 * 남던 문제가 구조적으로 사라진다.
 */
export async function bffLogout(): Promise<void> {
  // 이 뒤로는 401 이 와도 로그인으로 보내지 않는다(위 loggingOut 참조).
  loggingOut = true;
  let redirectUri = "/portal/";
  try {
    const response = await axios.post<{ redirectUri?: string }>(`${API_BASE}/bff/logout`);
    // 게이트웨이가 어디로 가야 할지 알려준다.
    //
    // ★ 인증 서버 로그아웃 주소일 수 있다. 게이트웨이 세션만 지우면 로그아웃이 절반만
    //   되기 때문이다 — 인증 서버 세션이 남아 다시 로그인할 때 비밀번호 없이 통과한다.
    //   여기서 응답을 무시하고 화면 주소로만 가면 그 절반이 영영 남는다.
    if (response.data?.redirectUri) {
      redirectUri = response.data.redirectUri;
    }
  } catch {
    // 세션을 끊지 못했더라도 화면은 로그아웃된 상태로 보내야 한다. 여기서 멈추면
    // 사용자가 로그아웃 버튼을 눌렀는데 아무 일도 일어나지 않는다.
  }
  window.location.assign(redirectUri);
}

/** {@link fetchBffSession} 의 응답. 토큰은 담기지 않는다 — 그것이 BFF 의 목적이다. */
export type BffSession = {
  userId: string;
  name?: string;
  email?: string;
};
